import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Save, ShieldCheck } from 'lucide-react';

const ROLES_EDITABLE = ['manager', 'office_staff', 'receptionist', 'trader', 'auditor'];
const ROLE_LABELS = { manager: 'Manager', office_staff: 'Office Staff', receptionist: 'Receptionist', trader: 'Trader', auditor: 'Auditor' };

export default function PermissionsPage() {
  const { refreshPermissions } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRole] = useState('manager');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const { data } = await api.get('/permissions');
        const permMap = {};
        data.permissions.forEach(p => { permMap[p.role] = p.permissions; });
        setPermissions(permMap);
        setModules(data.modules);
      } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
      finally { setLoading(false); }
    };
    fetchPerms();
  }, []);

  const togglePerm = (module, action) => {
    setPermissions(prev => ({
      ...prev,
      [activeRole]: {
        ...prev[activeRole],
        [module]: { ...prev[activeRole]?.[module], [action]: !prev[activeRole]?.[module]?.[action] }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/permissions/${activeRole}`, { role: activeRole, permissions: permissions[activeRole] });
      // Immediately refresh the current admin's own permission map so any
      // sidebar / route guard picks up the change without waiting for the
      // 30-second poll (other logged-in users of that role will refresh on
      // their next poll or tab-focus event).
      try { await refreshPermissions(); } catch (err) { console.warn('refreshPermissions failed:', err); /* non-blocking */ }
      toast.success(`Permissions saved for ${ROLE_LABELS[activeRole]} — changes go live for everyone within 30 seconds (or immediately on tab focus).`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const moduleLabels = {
    dashboard: 'Dashboard', branches: 'Branches', clients: 'Clients', employees: 'Employees',
    accounts: 'Trading Accounts', funds: 'Fund Management', pnl: 'Profit & Loss',
    performance: 'Performance Fees', kyc: 'KYC Verification', attendance: 'Attendance',
    reports: 'Reports', settings: 'Settings', audit: 'Audit Logs', permissions: 'Permissions',
    branding: 'Branding', revenue: 'Revenue', expenses: 'Expenses', company_pnl: 'Company P&L',
    agreements: 'Agreements',
    trading: 'Spot Trading', futures: 'Futures Trading', wallet: 'Wallet Transfer',
    academy: 'Quantora Academy', fund_schemes: 'Fund Schemes / NAV',
    payment_settings: 'Payment Settings', data_export: 'Data Export',
    accounting: 'Accounting & GST',
  };

  return (
    <div className="space-y-6" data-testid="permissions-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Permissions</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Configure role-based access control for each module</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="save-permissions-btn">
          <Save size={16} className="mr-2" /> Save Permissions
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <ShieldCheck size={16} className="text-brand-text-secondary" />
        <span className="text-xs text-brand-text-secondary">Super Admin & Admin have full access (not editable)</span>
      </div>

      {/* Role Tabs */}
      <div className="flex gap-2 flex-wrap">
        {ROLES_EDITABLE.map(r => (
          <Button key={r} variant={activeRole === r ? 'default' : 'outline'} size="sm" onClick={() => setActiveRole(r)}
            className={`rounded-sm text-xs ${activeRole === r ? 'bg-brand-primary text-white' : 'border-brand-border'}`} data-testid={`perm-tab-${r}`}>
            {ROLE_LABELS[r]}
          </Button>
        ))}
      </div>

      {loading ? (
        <Card className="border border-brand-border bg-white"><CardContent className="p-12 text-center text-brand-text-secondary">Loading...</CardContent></Card>
      ) : (
        <Card className="border border-brand-border bg-white">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-brand-text-primary flex items-center gap-2">
              <Badge className="text-xs bg-brand-primary/10 text-brand-primary border-0">{ROLE_LABELS[activeRole]}</Badge>
              Module Permissions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="permissions-table">
                <thead>
                  <tr className="border-b border-brand-border bg-brand-bg/50">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary w-48">Module</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">View</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">Add</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">Edit</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map(mod => {
                    const modPerms = permissions[activeRole]?.[mod] || {};
                    return (
                      <tr key={mod} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                        <td className="py-3 px-4 font-medium text-brand-text-primary">{moduleLabels[mod] || mod}</td>
                        {['view', 'add', 'edit', 'delete'].map(action => (
                          <td key={action} className="py-3 px-4 text-center">
                            <Switch
                              checked={!!modPerms[action]}
                              onCheckedChange={() => togglePerm(mod, action)}
                              data-testid={`perm-${mod}-${action}`}
                              className="data-[state=checked]:bg-brand-primary"
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
